/**
 * Deserialized view classes.
 */

import { NinePatchChunk } from './android/9patch';
import {
  ViewStateProto,
  CanvasOpAndroid,
  CanvasOpIOS,
  CanvasOpMobileMock,
  PorterDuffMode,
  ShaderType,
  TileMode,
  FrameBundleRootProto,
  FrameRootProto,
  FrameBundlePlatform,
  Flags,
  FlagsIOS,
} from '@fullstory/mobile-flatbuffers';
import { AssetData, IOSImageParams } from './resources';
import { serializeSelectorPart } from '@fullstory/selectors';
import { SelectorFactory } from './selector_factory';
import { SelectorPart } from '@fullstory/proto/fs/selectors';
import { Matrix } from './matrix';

export type ObjectId = string;
export interface ObjectIdMap<V> {
  [id: string]: V;
}

export class Point {
  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  public static ZERO() {
    return POINT_ZERO;
  }

  x: number;
  y: number;
}

const POINT_ZERO = new Point(0, 0);

export class Rect {
  constructor(x1: number, y1: number, x2: number, y2: number) {
    this.x1 = x1;
    this.y1 = y1;
    this.x2 = x2;
    this.y2 = y2;
  }

  x1: number;
  y1: number;
  x2: number;
  y2: number;

  public static ZERO() {
    return RECT_ZERO;
  }

  width() {
    return this.x2 - this.x1;
  }

  height() {
    return this.y2 - this.y1;
  }

  equalSized(r: Rect) {
    return this.height() == r.height() && this.width() == r.width();
  }

  isEmpty() {
    return this.width() == 0 || this.height() == 0;
  }

  scale(x: number, y: number): Rect {
    return new Rect(this.x1 * x, this.y1 * y, this.x2 * x, this.y2 * y);
  }

  boundingBoxUnion(r: Rect) {
    this.x1 = Math.min(this.x1, r.x1);
    this.y1 = Math.min(this.y1, r.y1);
    this.x2 = Math.max(this.x2, r.x2);
    this.y2 = Math.max(this.y2, r.y2);
  }

  translated(x: number, y: number): Rect {
    return new Rect(this.x1 + x, this.y1 + y, this.x2 + x, this.y2 + y);
  }

  transformedAABB(matrix: number[]): Rect {
    const a = Matrix.transformPoint(matrix, this.x1, this.y1);
    const b = Matrix.transformPoint(matrix, this.x1, this.y2);
    const c = Matrix.transformPoint(matrix, this.x2, this.y1);
    const d = Matrix.transformPoint(matrix, this.x2, this.y2);
    return new Rect(
      Math.min(a[0], b[0], c[0], d[0]),
      Math.min(a[1], b[1], c[1], d[1]),
      Math.max(a[0], b[0], c[0], d[0]),
      Math.max(a[1], b[1], c[1], d[1]),
    );
  }

  contains(x: number, y: number): boolean {
    return this.x1 <= x && x <= this.x2 && this.y1 <= y && y <= this.y2;
  }
}

const RECT_ZERO = new Rect(0, 0, 0, 0);

export enum BitmapType {
  Standard,
  Masked,
  RasterizedVector,
}

export class Bitmap {
  static MISSING: Bitmap = new Bitmap();
  width: number;
  height: number;
  density: number;
  url: string;
  img: HTMLImageElement;
  loaded?: Promise<void>;
  ninePatch: NinePatchChunk;
  asset: string;
  params: IOSImageParams;
  dominantColor: string;
  type: BitmapType;
  loadFailed = false;

  isValid(): boolean {
    // we always need these to be considered valid
    const initialCheck = !!(this != Bitmap.MISSING && this.width && this.height);
    if (!initialCheck) {
      return false;
    }

    if (this.type == BitmapType.Masked) {
      return true;
    }

    // Masked Bitmaps will never have the `img` field set, so only check for it after we confirm that this Bitmap
    // is not masked.
    return !!(this.img && this.img.height && this.img.width);
  }
}

export class CanvasShader {
  type: ShaderType;
  p1: Point;
  p2: Point;
  r: number;
  colors: string[];
  positions: number[];
  tileMode: TileMode;
}

export class CanvasPaint {
  isDefault: boolean; // for less verbose debugging
  color: string;
  alpha: number;
  style: number;
  textStyle: number;
  size: number;
  align: number;
  textBounds: Point;
  strokeWidth: number;
  bitmap: Bitmap;
  shader: CanvasShader;
  shaderMatrix: number[];
  tileX: TileMode;
  tileY: TileMode;
  typeface: AssetData;
  fillRule: CanvasFillRule;

  porterDuffColor: string;
  porterDuffColorMode: PorterDuffMode;

  porterDuffXferMode: PorterDuffMode;
}

export abstract class Canvas<OP> {
  op: OP;
  point: Point;
  text: string;
  bitmap: Bitmap;

  src: Rect;
  dest: Rect;
  viewId: ObjectId;
}

export class CanvasIOS extends Canvas<CanvasOpIOS> {
  matrix: number[];

  flag1: number;
  value: number;
  textSize: number;
  textRender: number;
  textFont: string;
  textCharSpacing: number;
  textBounds: Point;
  textPrivate: boolean;
  ninePatch: Rect;
  lineWidth: number;
  scaleOverride: number;
  shader: CanvasShader;

  point2: Point;
  point3: Point;

  color: string;
}

export class CanvasAndroid extends Canvas<CanvasOpAndroid> {
  paint: CanvasPaint;

  path: any;

  matrix: number[];

  flag1: number;
  value: number;
  textPrivate: boolean;
}

export class CanvasMobileMock extends Canvas<CanvasOpMobileMock> {
  name: string;
  p: [any];
}

export class View {
  frame: Frame;
  root: boolean;
  uniqueId: ObjectId;
  stringId: string;

  flags: number;
  isWebView: boolean;
  isBlocked: boolean;

  // iOS-only: represents the internal filter that may be applied
  filter: string;

  // Currently iOS-only: used if we need to composite one of the child view in a non-standard way
  hasComplexCompositingChild: boolean;

  // iOS-only: the color we should tint this view
  tintColor: string;

  position: Rect;
  clipmask: Rect;
  alpha: number;

  matrix: number[];

  scroll: Point;
  scrollF: Point;

  canvas: Canvas<number>[];

  windowType: number;
  windowFlags: number;

  hasCrop: string;
  // viewStateText is whatever string we put in the view state proto during capture
  viewStateText: string;
  // textContent is a best-effort representation of the view's text used as a heuristic
  // to possibly bail out during interpolation if too much has changed
  textContent: string;

  children: View[];

  // Note that this is `undefined` by default.  So `undefined` means 'please
  // refresh the cache', `null` means 'this view doesn't really have a
  // selector', and otherwise it's a `SelectorPart`.
  private cachedSelectorPart: SelectorPart | null;

  // whether this view applies its position rectangle as a clipping region to its descendants
  // only used for inspect mode logic, actual clipping is handled separately
  // value is set in reprocessCanvas, since it differs by platform
  clipsChildrenToBounds: boolean;
  // an approximate bounding box for this view and its descendants,
  // in the coordinates of the this view (so 0,0 is at this view's origin)
  // does not account for the matrix field of any view
  inclusiveBounds: Rect;
  // whether inclusiveBounds extends beyond this view's position rectangle
  hasChildOutsideBounds: boolean;

  public constructor(private proto: ViewStateProto, private selectorFactory: SelectorFactory) {}

  public flag(flag: number): boolean {
    return (this.flags & (1 << flag)) != 0;
  }

  isMasked(): boolean {
    const maskedFlag =
      this.frame.bundle.platform() === FrameBundlePlatform.IOS ? FlagsIOS.MASKED : Flags.MASKED;
    return this.flag(maskedFlag);
  }

  findChildRecursive(uniqueId: ObjectId): View {
    for (let i = 0; i < this.children.length; i++) {
      const child = this.children[i];
      if (child.uniqueId === uniqueId) return child;
      const childResult = child.findChildRecursive(uniqueId);
      if (childResult) return childResult;
    }
    return null;
  }

  getSelectorRule(): SelectorPart | null {
    if (this.cachedSelectorPart === undefined) {
      this.cachedSelectorPart = this.selectorFactory.compute(this.proto, this.root);
    }
    return this.cachedSelectorPart;
  }

  asSelector(): string {
    if (this.getSelectorRule() == null) {
      /* This is used for diagnostics only, so it's OK that this is not really a selector. */
      return 'null (framework-internal view)';
    }
    return serializeSelectorPart(this.getSelectorRule());
  }

  /**
   * Produces a concatenated string of all the text of this view and its descendants.
   * Reference backend behavior: https://github.com/cowpaths/mn/blob/dad9a5b6428fa033a6e9c16388a0e7b5826e7cee/projects/fullstory/go/src/fs/cook/viewtree/view.go#L385-L400
   */
  innerTextRecursive(): string {
    let innerText = '';
    this.walkTree(view => {
      const viewInnerText = view.innerText();
      if (viewInnerText) {
        // only add a space if we already have text and this view has text
        if (innerText.length > 0) {
          innerText += ' ';
        }
        innerText += viewInnerText;
      }
      return true;
    });
    return innerText;
  }

  innerText(): string {
    if (this.isMasked()) {
      return '*'.repeat(this.viewStateText.length);
    }
    return this.viewStateText;
  }

  walkTree(onView: (view: View) => boolean) {
    if (!onView(this)) {
      return;
    }
    this.children.forEach(v => v.walkTree(onView));
  }

  isDescendant(view: View): boolean {
    return !!view.findChildRecursive(this.uniqueId);
  }
}

export class Frame {
  bundle: FrameBundleRootProto;
  frame: FrameRootProto;
  index: number;
  bundleIndex: number;
  keyboardEvents: KeyboardVisibilityEvent[];
  timestamp: number;
  density: number;
  idleFramesSkipped: number;
  identicalFramesSkipped: number;
  window: Rect;
  topView: View;
  touches: Touches[];
  keys: Keys;
  bitmaps: { [imageId: number]: Bitmap };
  fonts: { [fontPath: string]: AssetData };
  // The "right" view for the root of the current screen
  // On iOS (UIKit), this maps to the view that is associated with the outermost view controller (i.e., the currently active screen name).
  // May be "0" if no such view is identified.
  // In the inspector, this is used to generate the ElementProposal.logicalPageContentRoot selector.
  screenRootViewId64: ObjectId;
  private viewsById: ObjectIdMap<View> = {};
  _fudgeIOSTextBounds = false;

  findViewById(uniqueId: ObjectId): View {
    return this.viewsById[uniqueId];
  }

  trackView(view: View) {
    this.viewsById[view.uniqueId] = view;
  }

  trackViewAndChildren(view: View) {
    this.trackView(view);
    view.children.forEach(v => this.trackViewAndChildren(v));
  }
}

export class KeyboardVisibilityEvent {
  constructor(readonly visible: boolean, readonly timestamp: number) {}
}

export class Keys {
  keys: number[];
}

export class Touches {
  id: ObjectId;
  isNew: boolean;
  isDown: boolean;
  private touches: { p1: Point; p2: Point; t1: Point; t2: Point };

  constructor(
    id: ObjectId,
    touches: { p1: Point; p2: Point; t1: Point; t2: Point },
    isNew: boolean,
    isDown: boolean,
  ) {
    this.id = id;
    this.touches = touches;
    this.isNew = isNew;
    this.isDown = isDown;
  }

  touchAt(a: number): Point {
    // Hermite spline basis functions.
    const a3 = a * a * a,
      a2 = a * a;
    const h1 = 2 * a3 + -3 * a2 + 1;
    const h2 = -2 * a3 + 3 * a2;
    const h3 = a3 + -2 * a2 + a;
    const h4 = a3 + -1 * a2;

    const x =
      h1 * this.touches.p1.x +
      h2 * this.touches.p2.x +
      h3 * this.touches.t1.x +
      h4 * this.touches.t2.x;
    const y =
      h1 * this.touches.p1.y +
      h2 * this.touches.p2.y +
      h3 * this.touches.t1.y +
      h4 * this.touches.t2.y;

    return { x, y };
  }
}
